import { Expense } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import { ScannerResultExpense, useScanReceipt } from './scanner';
import { FirestorageFileUrl, uploadReceipts } from './uploader';
import {
  MeteredPaywallActions,
  useHandleMeteredAction,
} from '../../../hooks/useHandleMeteredAction';
import { isBlockingOverlayOpen, isEditableTarget } from '../../../lib/keyboard';
import { ReceiptPageControl } from '../ReceiptPageControl.component';
import { ZoomableImage } from '../ZoomableImage.component';

export const ReceiptViewer: React.FC<
  React.PropsWithChildren<{
    receipts: string[];
    onChange?: (receipts: string[]) => void;
    selectedReceiptIndex: number;
    setSelectedReceiptIndex: (receiptIndex: number) => void;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    isNewExpense?: boolean;
    setExpense?: React.Dispatch<React.SetStateAction<Partial<Expense>>>;
    /** Blocks every way of adding a receipt while still letting the existing ones be viewed. */
    locked?: boolean;
  }>
> = ({
  receipts,
  onChange,
  selectedReceiptIndex,
  setSelectedReceiptIndex,
  setLoading,
  isNewExpense = false,
  setExpense,
  locked = false,
}) => {
  const scanReceipt = useScanReceipt();
  const handleMeteredScan = useHandleMeteredAction(MeteredPaywallActions.SCAN_RECEIPT);
  const categories = Data.expenseCategories.use();

  function updateReceipts(imageUrls: FirestorageFileUrl[]) {
    const allReceipts: string[] = [...receipts, ...imageUrls];
    setSelectedReceiptIndex(allReceipts.length - 1);
    if (onChange) {
      onChange(allReceipts);
    }
  }

  function showNextReceipt() {
    const newIndex = selectedReceiptIndex + 1;
    if (newIndex === receipts.length) {
      setSelectedReceiptIndex(0);
    } else {
      setSelectedReceiptIndex(newIndex);
    }
  }

  function showPreviousReceipt() {
    const newIndex = selectedReceiptIndex - 1;
    if (newIndex < 0) {
      setSelectedReceiptIndex(receipts.length - 1);
    } else {
      setSelectedReceiptIndex(newIndex);
    }
  }

  React.useEffect(() => {
    if (receipts.length <= 1) {
      return;
    }
    function onKeyDown(event: KeyboardEvent) {
      if (isEditableTarget(event.target) || isBlockingOverlayOpen()) {
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNextReceipt();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPreviousReceipt();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedReceiptIndex, receipts.length]);

  const uncategorizedCategory = categories.find((c) => c.value.name === 'Uncategorized');
  const receiptUri = receipts[selectedReceiptIndex] ?? '';

  /** Scans the first receipt and prefills the expense from it, marking the expense as a scan. */
  async function scanFirstReceipt(files: FileList) {
    setLoading(true);
    let scannedExpense: ScannerResultExpense | undefined;
    try {
      if (files[0]) {
        scannedExpense = await scanReceipt(files[0]);
      }
    } catch (err) {
      console.warn('Failed to scan receipt: ', err);
    }
    const uploadedReceipts = await uploadReceipts(files);
    setSelectedReceiptIndex(0);

    let vendorKey: string | undefined;
    if (scannedExpense?.vendor) {
      vendorKey = Data.vendors.create({ value: { name: scannedExpense.vendor } });
    }

    if (setExpense) {
      setExpense({
        receipts: uploadedReceipts,
        sourceType: 'scan',
        total: scannedExpense?.total,
        tax: scannedExpense?.tax,
        secondTax: scannedExpense?.secondTax,
        category: scannedExpense?.categoryKey ?? uncategorizedCategory?.key ?? '',
        vendor: vendorKey,
        paymentMethod: scannedExpense?.paymentMethodKey,
        date: scannedExpense?.date,
      });
    }
    setLoading(false);
  }

  /** Uploads the receipts and attaches them as they are, without scanning. */
  async function attachReceipts(files?: FileList | null) {
    setLoading(true);
    const uploadedReceipts = await uploadReceipts(files);
    updateReceipts(uploadedReceipts);
    setLoading(false);
  }

  async function onUpload(files?: FileList | null) {
    if (files && files.length === 1 && isNewExpense) {
      // Past the free scan limit the receipt is still attached, just not scanned; the wall opens
      // over it as an upsell rather than blocking the upload.
      await handleMeteredScan(
        () => scanFirstReceipt(files),
        () => void attachReceipts(files),
      );
      return;
    }
    await attachReceipts(files);
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    // The drop target covers the whole left column, so it has to refuse the file itself —
    // hiding the drop zone below only removes the invitation, not the target.
    if (locked) {
      return;
    }
    onUpload(event?.dataTransfer?.files);
  }

  return (
    <Layout.Column
      style={{ height: '100%', backgroundColor: '#CFD6E0' }}
      onDrop={onDrop}
      onDragOver={(event: DragEvent) => event.preventDefault()}
      justify
    >
      {!receipts.length ? (
        <EmptyReceiptPanel locked={locked} onUpload={onUpload} />
      ) : (
        <Layout.Column style={{ height: '100%', width: '100%' }} center>
          <Layout.Column style={{ height: '90vh', width: '90%' }}>
            <ZoomableImage key={selectedReceiptIndex} uri={receiptUri} />
          </Layout.Column>

          <ReceiptPageControl
            selectedReceiptIndex={selectedReceiptIndex}
            count={receipts.length}
            onPrevious={showPreviousReceipt}
            onNext={showNextReceipt}
            style={{ position: 'absolute', bottom: 32 }}
          />
        </Layout.Column>
      )}
    </Layout.Column>
  );
};

/**
 * What fills the left column when an expense has no receipts yet: the drop zone normally,
 * or a matching lock message when receipts can't be added.
 */
const EmptyReceiptPanel: React.FC<{
  locked: boolean;
  onUpload: (files?: FileList | null) => void;
}> = ({ locked, onUpload }) => {
  if (locked) {
    return (
      <Layout.Row center>
        <Layout.Column center style={{ margin: 32, padding: 32 }}>
          <Icon name="lock-closed-outline" size={23} color={theme.colors.secondary} />
          <Spacer.Vertical size={12} />
          <OpenSans.Secondary weight="bold-700">
            {getTranslation('Receipts are locked')}
          </OpenSans.Secondary>
          <Spacer.Vertical size={4} />
          <OpenSans.Secondary size="xs-12">
            {getTranslation('A manager or admin can add receipts to this expense')}
          </OpenSans.Secondary>
        </Layout.Column>
      </Layout.Row>
    );
  }

  return (
    <Layout.Row center>
      <Layout.PressableColumn bg="transparent">
        <label
          htmlFor="files"
          style={{
            borderStyle: 'dashed',
            borderWidth: 1,
            borderRadius: 6,
            borderColor: theme.colors.brandPrimary,
            backgroundColor: theme.colors.brandPrimaryXLight,
            margin: 32,
            padding: 32,
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Layout.Column center style={{ justifyContent: 'space-between' }}>
            <Icon name="receipt-outline" size={23} color={theme.colors.brandPrimary} />
            <Spacer.Vertical size={12} />
            <OpenSans.Pressable weight="bold-700">Drag & drop receipts</OpenSans.Pressable>
            <Spacer.Vertical size={4} />
            <OpenSans.Pressable size="xs-12">We will scan details automatically</OpenSans.Pressable>
            <input
              type="file"
              id="files"
              multiple
              accept=".png,.jpg,.jpeg,.heic,.heif,.pdf"
              onChange={(event) => onUpload(event.target.files)}
              style={{ display: 'none' }}
            />
          </Layout.Column>
        </label>
      </Layout.PressableColumn>
    </Layout.Row>
  );
};
